/**
 * Data Management Feature - Main Export
 *
 * This module provides the main exports for the new browser-based data management feature,
 * including export/import services, components, and utilities for trip data operations.
 * Replaces the backup directory system with standard web download/upload patterns.
 */

// Import services
import { exportService } from './services/exportService';
import { importService } from './services/importService';
import { backupService } from './services/backupService';
import { settingsService } from './services/settingsService';

// Import new PDF export services
import { pdfExportService } from './services/pdfExportService';
import { pageContextDetector } from './services/pageContextDetector';
import { pdfLayoutEngine } from './services/pdfLayoutEngine';

// Import new export/import services
import itineraryExportService from './services/itineraryExportService';
import tripExportService from './services/tripExportService';
import tripImportService from './services/tripImportService';
import { registerImportLinkRegistryWrapper } from './services/importServiceWithRegistry.js';

// Import format handlers
import { jsonFormatHandler } from './formats/json';
import { csvFormatHandler } from './formats/csv';
import { pdfFormatHandler } from './formats/pdf';
import excelGenerator from './formats/excel';
import zipProcessor from './formats/zip';

// Import components
import { ExportImportModal } from './components/ExportImportModal';
import { ExportWizard } from './components/ExportWizard';
import { DataRestoreModal } from './components/DataRestoreModal';
import { PDFConfirmationModal } from './components/PDFConfirmation';
import TripExportProgressModal from './components/TripExportProgress';

// Import new modal components
import { ItineraryExportModal } from './components/ItineraryExport';
import TripExportModal from './components/TripExport/TripExportModal';
import TripImportModal from './components/TripImport/TripImportModal';

/**
 * Initialize all data management services
 * This function should be called during application startup
 */
export const initDataManagementServices = () => {
  exportService.init();
  importService.init();
  registerImportLinkRegistryWrapper();
  backupService.init();
  settingsService.init();
  // PDF services don't need initialization
  console.log('Data Management services initialized');
};

/**
 * Data Management Service Interface
 * Provides a unified interface for all data management operations
 */
export const dataManagement = {
  // Service instances
  export: exportService,
  import: importService,
  backup: backupService,
  settings: settingsService,

  // PDF export services
  pdfExport: pdfExportService,
  pageContext: pageContextDetector,
  pdfLayout: pdfLayoutEngine,

  // New export/import services
  itineraryExport: itineraryExportService,
  tripExport: tripExportService,
  tripImport: tripImportService,

  // Format handlers
  formats: {
    json: jsonFormatHandler,
    csv: csvFormatHandler,
    pdf: pdfFormatHandler,
    excel: excelGenerator,
    zip: zipProcessor
  },

  // Export methods - Browser download patterns
  async exportTrip(tripId, userId, format = 'json', options = {}) {
    return exportService.downloadTripExport({
      tripId,
      userId,
      format,
      scope: 'complete',
      ...options
    });
  },

  async exportPages(tripId, userId, pageIds, format = 'json', options = {}) {
    return exportService.downloadTripExport({
      tripId,
      userId,
      format,
      scope: 'pages',
      entityIds: pageIds,
      ...options
    });
  },

  async exportDays(tripId, userId, dayIds, format = 'json', options = {}) {
    return exportService.downloadTripExport({
      tripId,
      userId,
      format,
      scope: 'days',
      entityIds: dayIds,
      ...options
    });
  },

  async exportUserData(userId, format = 'json', options = {}) {
    // For user data export, we'll need to implement a different method
    // that gathers data from all user trips
    return exportService.downloadUserDataExport({
      userId,
      format,
      scope: 'user',
      ...options
    });
  },

  // Import methods - File upload patterns
  async importTrip(file, userId, importMode = 'new', options = {}) {
    return importService.uploadTripImport({
      file,
      userId,
      importMode, // 'new', 'merge', 'replace'
      ...options
    });
  },

  // Legacy import method for DataRestoreModal compatibility
  async importData(userId, source, format, options = {}) {
    // Convert old-style parameters to new file-based approach
    if (typeof source === 'string') {
      // Create a File-like object from JSON string
      const blob = new Blob([source], { type: 'application/json' });
      const file = new File([blob], 'import.json', { type: 'application/json' });

      return this.importTrip(file, userId, 'new', options);
    } else if (source instanceof File) {
      return this.importTrip(source, userId, 'new', options);
    } else {
      throw new Error('Invalid source type for import');
    }
  },

  // File validation method
  async validateImportFile(file, importType = 'complete') {
    return importService.validateImportFile(file, importType);
  },

  // Generate import preview
  async generateImportPreview(file) {
    return importService.generateImportPreview(file);
  },

  async importPages(file, tripId, userId, options = {}) {
    return importService.uploadSelectiveImport({
      file,
      tripId,
      userId,
      scope: 'pages',
      ...options
    });
  },

  async importDays(file, tripId, userId, options = {}) {
    return importService.uploadSelectiveImport({
      file,
      tripId,
      userId,
      scope: 'days',
      ...options
    });
  },

  // Utility methods
  getSupportedFormats() {
    return ['json', 'csv', 'pdf'];
  },

  getFormatHandler(format) {
    return this.formats[format] || null;
  },

  validateFormat(format) {
    return this.getSupportedFormats().includes(format);
  },

  // Backup methods - Auto JSON format for complete data fidelity
  async createBackup(userId, tripId = null, directory = null) {
    // Backups always use JSON format for complete data fidelity
    if (tripId) {
      // Single trip backup
      return exportService.downloadTripExport({
        tripId,
        userId,
        format: 'json', // Hardcoded for backups
        scope: 'complete'
      });
    } else {
      // User data backup
      return exportService.downloadUserDataExport({
        userId,
        format: 'json' // Hardcoded for backups
      });
    }
  },

  // PDF Export methods
  async exportCurrentPageAsPDF(context) {
    return pdfExportService.exportCurrentPage(context);
  },

  async getPDFExportPreview(context) {
    return pdfExportService.getExportPreview(context);
  }
};

// Export individual services for direct access
export {
  exportService,
  importService,
  backupService,
  settingsService,
  pdfExportService,
  pageContextDetector,
  pdfLayoutEngine,
  jsonFormatHandler,
  csvFormatHandler,
  pdfFormatHandler,
  itineraryExportService,
  tripExportService,
  tripImportService,
  registerImportLinkRegistryWrapper,
  excelGenerator,
  zipProcessor
};

// Export components
export {
  ExportImportModal,
  ExportWizard,
  DataRestoreModal,
  PDFConfirmationModal,
  ItineraryExportModal,
  TripExportModal,
  TripImportModal,
  TripExportProgressModal
};

// Default export
export default dataManagement;